"use client";
import React, { useState, useEffect, useRef } from "react";
import { Editor } from "@tiptap/react";
import ImageUploadModal from "./image-upload-modal";
import AssetSelectorModal from "./asset-selector-modal";
import {
  LuUndo2,
  LuRedo2,
  LuChevronDown,
  LuBold,
  LuItalic,
  LuUnderline,
  LuStrikethrough,
  LuCode,
  LuAlignLeft,
  LuAlignCenter,
  LuAlignRight,
  LuAlignJustify,
  LuLink,
  LuUnlink,
  LuList,
  LuListOrdered,
  LuListTodo,
  LuImage,
  LuChartColumn,
  LuTable,
  LuQuote,
  LuMinus,
  LuDownload,
} from "react-icons/lu";
import { FiMoreHorizontal } from "react-icons/fi";
import { exportDocx, exportPdf } from "~/lib/network/analysis-document";

interface TiptapToolbarProps {
  editor: Editor | null;
  sessionId: string;
  isSaving?: boolean;
  hasUnsavedChanges?: boolean;
  lastSaved?: Date | null;
  saveError?: string | null;
}

const TiptapToolbar = ({
  editor,
  sessionId,
  isSaving,
  hasUnsavedChanges,
  lastSaved,
  saveError,
}: TiptapToolbarProps) => {
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showAssetSelector, setShowAssetSelector] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Enhanced time formatting function with weeks, months, years
  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Handle negative values (future dates due to clock skew)
    if (seconds < 0) return "just now";
    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`; // < 1 week
    if (seconds < 2629746) return `${Math.floor(seconds / 604800)} weeks ago`; // < 1 month (30.44 days)
    if (seconds < 31556952)
      return `${Math.floor(seconds / 2629746)} months ago`; // < 1 year (365.25 days)
    return `${Math.floor(seconds / 31556952)} years ago`;
  };

  if (!editor) return null;

  const getCurrentBlockLabel = () => {
    if (editor.isActive("heading", { level: 1 })) return "Heading 1";
    if (editor.isActive("heading", { level: 2 })) return "Heading 2";
    if (editor.isActive("heading", { level: 3 })) return "Heading 3";
    if (editor.isActive("heading", { level: 4 })) return "Heading 4";
    if (editor.isActive("heading", { level: 5 })) return "Heading 5";
    if (editor.isActive("heading", { level: 6 })) return "Heading 6";
    if (editor.isActive("codeBlock")) return "Code Block";
    if (editor.isActive("bulletList")) return "Bullet List";
    if (editor.isActive("orderedList")) return "Numbered List";
    if (editor.isActive("taskList")) return "Task List";
    if (editor.isActive("blockquote")) return "Quote";
    return "Paragraph";
  };

  const getCurrentAlignment = () => {
    if (editor.isActive({ textAlign: "center" })) return "center";
    if (editor.isActive({ textAlign: "right" })) return "right";
    if (editor.isActive({ textAlign: "justify" })) return "justify";
    return "left";
  };

  const getCurrentListType = () => {
    if (editor.isActive("bulletList")) return "bullet";
    if (editor.isActive("orderedList")) return "ordered";
    if (editor.isActive("taskList")) return "task";
    return "none";
  };

  const isDropdownDisabled = () => {
    // Disable dropdown for block types that aren't supported by the dropdown options
    return (
      editor.isActive("bulletList") ||
      editor.isActive("orderedList") ||
      editor.isActive("taskList") ||
      editor.isActive("codeBlock") ||
      editor.isActive("blockquote")
    );
  };

  const insertLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = prompt("Enter URL:", previousUrl);

    if (url === null) {
      return;
    }

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertImage = () => {
    setShowImageUpload(true);
  };

  const insertAssetImage = () => {
    setShowAssetSelector(true);
  };

  const handleImageUploaded = (imageId: string, filename: string) => {
    if (editor) {
      editor
        .chain()
        .focus()
        .setUploadedImage({
          imageId,
          sessionId,
          alt: filename,
        })
        .run();
    }
  };

  const handleImageUrl = (url: string) => {
    if (editor) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const handleAssetSelected = (
    sessionId: string,
    filename: string,
    title: string
  ) => {
    if (editor) {
      editor
        .chain()
        .focus()
        .insertAssetImage({
          sessionId,
          filename,
          alt: title,
        })
        .run();
    }
  };

  const insertTable = () => {
    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  };

  const handleDocxDownload = async () => {
    if (!editor || isDownloading) return;

    const startTime = performance.now();
    
    try {
      setIsDownloading(true);

      // Get TipTap JSON from editor
      const tiptapJson = editor.getJSON();
      const contentNodes = tiptapJson?.content?.length || 0;
      const jsonSize = new Blob([JSON.stringify(tiptapJson)]).size / 1024;
      
      console.log(`Starting DOCX export for session ${sessionId}: contentNodes=${contentNodes}, jsonSize=${jsonSize.toFixed(1)}KB`);
      
      // Call export API
      const apiStartTime = performance.now();
      const blob = await exportDocx(sessionId, tiptapJson);
      const apiTime = performance.now() - apiStartTime;
      
      const docxSize = blob.size / 1024;
      console.log(`DOCX API call completed for session ${sessionId}: size=${docxSize.toFixed(1)}KB, apiTime=${(apiTime/1000).toFixed(2)}s`);
      
      // Create download link
      const downloadStartTime = performance.now();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `analysis_document_${sessionId}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      const downloadTime = performance.now() - downloadStartTime;
      const totalTime = performance.now() - startTime;
      
      console.log(`DOCX export completed for session ${sessionId}: filename=analysis_document_${sessionId}.docx, downloadTime=${downloadTime.toFixed(0)}ms, totalTime=${(totalTime/1000).toFixed(2)}s`);
      
    } catch (error) {
      const totalTime = performance.now() - startTime;
      console.error(`DOCX export failed for session ${sessionId}: error=${(error as Error)?.message || String(error)}, totalTime=${(totalTime/1000).toFixed(2)}s`, error);
      // You could add toast notification here
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePdfDownload = async () => {
    if (!editor || isDownloading) return;

    const startTime = performance.now();
    
    try {
      setIsDownloading(true);
      
      // Get TipTap JSON from editor
      const tiptapJson = editor.getJSON();
      const contentNodes = tiptapJson?.content?.length || 0;
      const jsonSize = new Blob([JSON.stringify(tiptapJson)]).size / 1024;
      
      console.log(`Starting PDF export for session ${sessionId}: contentNodes=${contentNodes}, jsonSize=${jsonSize.toFixed(1)}KB`);
      
      // Call export API
      const apiStartTime = performance.now();
      const blob = await exportPdf(sessionId, tiptapJson);
      const apiTime = performance.now() - apiStartTime;
      
      const pdfSize = blob.size / 1024;
      console.log(`PDF API call completed for session ${sessionId}: size=${pdfSize.toFixed(1)}KB, apiTime=${(apiTime/1000).toFixed(2)}s`);
      
      // Create download link
      const downloadStartTime = performance.now();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analysis_document_${sessionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      const downloadTime = performance.now() - downloadStartTime;
      const totalTime = performance.now() - startTime;
      
      console.log(`PDF export completed for session ${sessionId}: filename=analysis_document_${sessionId}.pdf, downloadTime=${downloadTime.toFixed(0)}ms, totalTime=${(totalTime/1000).toFixed(2)}s`);
      
    } catch (error) {
      const totalTime = performance.now() - startTime;
      console.error(`PDF export failed for session ${sessionId}: error=${(error as Error)?.message || String(error)}, totalTime=${(totalTime/1000).toFixed(2)}s`, error);
      // You could add toast notification here
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <div className="@container bg-base-100 border-b border-base-200 p-1">
        {/* Row 1: Document Structure & Basic Formatting */}
        <div className="flex items-center pb-2">
          {/* Undo/Redo */}
          <div className="join">
            <button
              className="btn btn-neutral btn-ghost btn-sm btn-square join-item"
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().chain().focus().undo().run()}
              title="Undo"
            >
              <LuUndo2 className="w-4 h-4" />
            </button>
            <button
              className="btn btn-neutral btn-ghost btn-sm btn-square join-item"
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().chain().focus().redo().run()}
              title="Redo"
            >
              <LuRedo2 className="w-4 h-4" />
            </button>
          </div>

          <div className="divider divider-horizontal"></div>

          {/* Heading Dropdown */}
          <div
            className={`dropdown ${
              isDropdownDisabled() ? "dropdown-disabled" : ""
            }`}
          >
            <div
              tabIndex={isDropdownDisabled() ? -1 : 0}
              role="button"
              className={`btn btn-neutral btn-sm ${
                isDropdownDisabled()
                  ? "btn-disabled opacity-50 cursor-not-allowed"
                  : "btn-ghost"
              }`}
            >
              <span>{getCurrentBlockLabel()}</span>
              <LuChevronDown className="w-4 h-4 ml-1" />
            </div>
            {!isDropdownDisabled() && (
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-box z-[200] w-56 p-2 shadow border border-base-200"
              >
                <li>
                  <a
                    onClick={() => editor.chain().focus().setParagraph().run()}
                    className={`flex items-center ${
                      editor.isActive("paragraph") ? "menu-active" : ""
                    }`}
                  >
                    <span style={{ fontSize: "14px", fontWeight: "400" }}>
                      Paragraph
                    </span>
                    <span className="text-xs text-base-content/60 ml-auto">
                      Normal text
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    onClick={() =>
                      editor.chain().focus().toggleHeading({ level: 1 }).run()
                    }
                    className={`flex items-center ${
                      editor.isActive("heading", { level: 1 })
                        ? "menu-active"
                        : ""
                    }`}
                  >
                    <span style={{ fontSize: "20px", fontWeight: "700" }}>
                      Heading 1
                    </span>
                    <span className="text-xs text-base-content/60 ml-auto">
                      Large
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    onClick={() =>
                      editor.chain().focus().toggleHeading({ level: 2 }).run()
                    }
                    className={`flex items-center ${
                      editor.isActive("heading", { level: 2 })
                        ? "menu-active"
                        : ""
                    }`}
                  >
                    <span style={{ fontSize: "18px", fontWeight: "600" }}>
                      Heading 2
                    </span>
                    <span className="text-xs text-base-content/60 ml-auto">
                      Medium
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    onClick={() =>
                      editor.chain().focus().toggleHeading({ level: 3 }).run()
                    }
                    className={`flex items-center ${
                      editor.isActive("heading", { level: 3 })
                        ? "menu-active"
                        : ""
                    }`}
                  >
                    <span style={{ fontSize: "16px", fontWeight: "500" }}>
                      Heading 3
                    </span>
                    <span className="text-xs text-base-content/60 ml-auto">
                      Small
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    onClick={() =>
                      editor.chain().focus().toggleHeading({ level: 4 }).run()
                    }
                    className={`flex items-center ${
                      editor.isActive("heading", { level: 4 })
                        ? "menu-active"
                        : ""
                    }`}
                  >
                    <span style={{ fontSize: "15px", fontWeight: "500" }}>
                      Heading 4
                    </span>
                    <span className="text-xs text-base-content/60 ml-auto">
                      Extra Small
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    onClick={() =>
                      editor.chain().focus().toggleHeading({ level: 5 }).run()
                    }
                    className={`flex items-center ${
                      editor.isActive("heading", { level: 5 })
                        ? "menu-active"
                        : ""
                    }`}
                  >
                    <span style={{ fontSize: "14px", fontWeight: "500" }}>
                      Heading 5
                    </span>
                    <span className="text-xs text-base-content/60 ml-auto">
                      Tiny
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    onClick={() =>
                      editor.chain().focus().toggleHeading({ level: 6 }).run()
                    }
                    className={`flex items-center ${
                      editor.isActive("heading", { level: 6 })
                        ? "menu-active"
                        : ""
                    }`}
                  >
                    <span style={{ fontSize: "13px", fontWeight: "500" }}>
                      Heading 6
                    </span>
                    <span className="text-xs text-base-content/60 ml-auto">
                      Smallest
                    </span>
                  </a>
                </li>
              </ul>
            )}
          </div>

          {/* Text Formatting */}
          <div className="join">
            <button
              className={`btn btn-neutral btn-sm btn-square join-item ${
                editor.isActive("bold") ? "btn-active" : "btn-ghost"
              }`}
              onClick={() => editor.chain().focus().toggleBold().run()}
              disabled={!editor.can().chain().focus().toggleBold().run()}
              title="Bold"
            >
              <LuBold className="w-4 h-4" />
            </button>
            <button
              className={`btn btn-neutral btn-sm btn-square join-item ${
                editor.isActive("italic") ? "btn-active" : "btn-ghost"
              }`}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              disabled={!editor.can().chain().focus().toggleItalic().run()}
              title="Italic"
            >
              <LuItalic className="w-4 h-4" />
            </button>
            <button
              className={`btn btn-neutral btn-sm btn-square join-item ${
                editor.isActive("underline") ? "btn-active" : "btn-ghost"
              }`}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              disabled={!editor.can().chain().focus().toggleUnderline().run()}
              title="Underline"
            >
              <LuUnderline className="w-4 h-4" />
            </button>
            <button
              className={`btn btn-neutral btn-sm btn-square join-item ${
                editor.isActive("strike") ? "btn-active" : "btn-ghost"
              }`}
              onClick={() => editor.chain().focus().toggleStrike().run()}
              disabled={!editor.can().chain().focus().toggleStrike().run()}
              title="Strikethrough"
            >
              <LuStrikethrough className="w-4 h-4" />
            </button>
            <div className="dropdown">
              <div
                tabIndex={0}
                role="button"
                className={`btn btn-neutral btn-sm join-item ${
                  editor.isActive("codeBlock") || editor.isActive("code")
                    ? "btn-active"
                    : "btn-ghost"
                }`}
                title="Code"
              >
                <LuCode className="w-4 h-4" />
                <LuChevronDown className="w-2 h-2 ml-1" />
              </div>
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-box z-[200] w-40 p-2 shadow border border-base-200"
              >
                <li>
                  <a
                    onClick={() =>
                      editor.chain().focus().toggleCodeBlock().run()
                    }
                    className={`flex items-center ${
                      editor.isActive("codeBlock") ? "menu-active" : ""
                    }`}
                  >
                    Code Block
                  </a>
                </li>
                <li>
                  <a
                    onClick={() => editor.chain().focus().toggleCode().run()}
                    className={`flex items-center ${
                      editor.isActive("code") ? "menu-active" : ""
                    }`}
                  >
                    Inline Code
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="divider divider-horizontal"></div>
          <div className="dropdown">
            <div
              tabIndex={0}
              role="button"
              className="btn btn-neutral btn-sm btn-ghost"
              title="Text Alignment"
            >
              {getCurrentAlignment() === "left" && (
                <LuAlignLeft className="w-4 h-4" />
              )}
              {getCurrentAlignment() === "center" && (
                <LuAlignCenter className="w-4 h-4" />
              )}
              {getCurrentAlignment() === "right" && (
                <LuAlignRight className="w-4 h-4" />
              )}
              {getCurrentAlignment() === "justify" && (
                <LuAlignJustify className="w-4 h-4" />
              )}
              <LuChevronDown className="w-3 h-3 ml-1" />
            </div>
            <ul
              tabIndex={0}
              className="dropdown-content menu bg-base-100 rounded-box z-[200] w-40 p-2 shadow border border-base-200"
            >
              <li>
                <a
                  onClick={() =>
                    editor.chain().focus().setTextAlign("left").run()
                  }
                  className={`flex items-center ${
                    getCurrentAlignment() === "left" ? "menu-active" : ""
                  }`}
                >
                  <LuAlignLeft className="w-4 h-4 mr-2" />
                  Left
                </a>
              </li>
              <li>
                <a
                  onClick={() =>
                    editor.chain().focus().setTextAlign("center").run()
                  }
                  className={`flex items-center ${
                    getCurrentAlignment() === "center" ? "menu-active" : ""
                  }`}
                >
                  <LuAlignCenter className="w-4 h-4 mr-2" />
                  Center
                </a>
              </li>
              <li>
                <a
                  onClick={() =>
                    editor.chain().focus().setTextAlign("right").run()
                  }
                  className={`flex items-center ${
                    getCurrentAlignment() === "right" ? "menu-active" : ""
                  }`}
                >
                  <LuAlignRight className="w-4 h-4 mr-2" />
                  Right
                </a>
              </li>
              <li>
                <a
                  onClick={() =>
                    editor.chain().focus().setTextAlign("justify").run()
                  }
                  className={`flex items-center ${
                    getCurrentAlignment() === "justify" ? "menu-active" : ""
                  }`}
                >
                  <LuAlignJustify className="w-4 h-4 mr-2" />
                  Justify
                </a>
              </li>
            </ul>
          </div>
          {/* Lists */}
          <div className="dropdown">
            <div
              tabIndex={0}
              role="button"
              className="btn btn-neutral btn-sm btn-ghost"
              title="Lists"
            >
              {getCurrentListType() === "bullet" && (
                <LuList className="w-4 h-4" />
              )}
              {getCurrentListType() === "ordered" && (
                <LuListOrdered className="w-4 h-4" />
              )}
              {getCurrentListType() === "task" && (
                <LuListTodo className="w-4 h-4" />
              )}
              {getCurrentListType() === "none" && (
                <LuList className="w-4 h-4" />
              )}
              <LuChevronDown className="w-3 h-3 ml-1" />
            </div>
            <ul
              tabIndex={0}
              className="dropdown-content menu bg-base-100 rounded-box z-[200] w-44 p-2 shadow border border-base-200"
            >
              <li>
                <a
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  className={`flex items-center ${
                    getCurrentListType() === "bullet" ? "menu-active" : ""
                  }`}
                >
                  <LuList className="w-4 h-4 mr-2" />
                  Bullet List
                </a>
              </li>
              <li>
                <a
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  className={`flex items-center ${
                    getCurrentListType() === "ordered" ? "menu-active" : ""
                  }`}
                >
                  <LuListOrdered className="w-4 h-4 mr-2" />
                  Numbered List
                </a>
              </li>
              <li>
                <a
                  onClick={() => editor.chain().focus().toggleTaskList().run()}
                  className={`flex items-center ${
                    getCurrentListType() === "task" ? "menu-active" : ""
                  }`}
                >
                  <LuListTodo className="w-4 h-4 mr-2" />
                  Task List
                </a>
              </li>
            </ul>
          </div>

          {/* Link Management */}
          <div className="join">
            <button
              className={`btn btn-neutral btn-ghost btn-sm btn-square join-item ${
                editor.isActive("link") ? "btn-active" : ""
              }`}
              onClick={insertLink}
              title="Insert Link"
            >
              <LuLink className="w-4 h-4" />
            </button>
            {editor.isActive("link") && (
              <button
                className="btn btn-neutral btn-ghost btn-sm btn-square join-item"
                onClick={() => editor.chain().focus().unsetLink().run()}
                title="Remove Link"
              >
                <LuUnlink className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="divider divider-horizontal"></div>

          {/* Individual buttons - show when container is wide enough */}
          <div className="@[800px]:flex @[800px]:gap-1 hidden">
            <button
              className="btn btn-neutral btn-ghost btn-sm btn-square"
              onClick={insertImage}
              title="Insert Image"
            >
              <LuImage className="w-4 h-4" />
            </button>

            <button
              className="btn btn-neutral btn-ghost btn-sm btn-square"
              onClick={insertAssetImage}
              title="Insert Chart"
            >
              <LuChartColumn className="w-4 h-4" />
            </button>

            <button
              className="btn btn-neutral btn-ghost btn-sm btn-square"
              onClick={insertTable}
              disabled={!editor.can().chain().focus().insertTable().run()}
              title="Insert Table"
            >
              <LuTable className="w-4 h-4" />
            </button>

            <button
              className={`btn btn-neutral btn-ghost btn-sm btn-square ${
                editor.isActive("blockquote") ? "btn-active" : ""
              }`}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              title="Block Quote"
            >
              <LuQuote className="w-4 h-4" />
            </button>

            <button
              className="btn btn-neutral btn-ghost btn-sm btn-square"
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
              title="Horizontal Rule"
            >
              <LuMinus className="w-4 h-4" />
            </button>
          </div>

          {/* Dropdown - show when container is narrow */}
          <div className="@[800px]:hidden dropdown dropdown-end">
            <div
              tabIndex={0}
              role="button"
              className="btn btn-neutral btn-ghost btn-sm btn-square"
              title="More options"
            >
              <FiMoreHorizontal className="w-4 h-4" />
            </div>
            <ul
              tabIndex={0}
              className="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 p-2 shadow"
            >
              <li>
                <button
                  className="flex items-center gap-2"
                  onClick={insertImage}
                >
                  <LuImage className="w-4 h-4" />
                  Insert Image
                </button>
              </li>
              <li>
                <button
                  className="flex items-center gap-2"
                  onClick={insertAssetImage}
                >
                  <LuChartColumn className="w-4 h-4" />
                  Insert Chart
                </button>
              </li>
              <li>
                <button
                  className="flex items-center gap-2"
                  onClick={insertTable}
                  disabled={!editor.can().chain().focus().insertTable().run()}
                >
                  <LuTable className="w-4 h-4" />
                  Insert Table
                </button>
              </li>
              <li>
                <button
                  className={`flex items-center gap-2 ${
                    editor.isActive("blockquote") ? "active" : ""
                  }`}
                  onClick={() =>
                    editor.chain().focus().toggleBlockquote().run()
                  }
                >
                  <LuQuote className="w-4 h-4" />
                  Block Quote
                </button>
              </li>
              <li>
                <button
                  className="flex items-center gap-2"
                  onClick={() =>
                    editor.chain().focus().setHorizontalRule().run()
                  }
                >
                  <LuMinus className="w-4 h-4" />
                  Horizontal Rule
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Save Status & Export Section */}
        <div className="flex items-center justify-between text-xs text-base-content/60 mt-1">
          {/* Save Status */}
          <div>
            {isSaving && (
              <span className="flex items-center gap-1">
                <span className="loading loading-spinner loading-xs"></span>
                Saving...
              </span>
            )}
            {!isSaving && lastSaved && !hasUnsavedChanges && (
              <span className="text-success">
                ✓ Saved {formatRelativeTime(lastSaved)}
              </span>
            )}
            {hasUnsavedChanges && !isSaving && (
              <span className="text-warning">• Unsaved changes</span>
            )}
            {saveError && <span className="text-error">⚠ Failed to save</span>}
          </div>

          {/* Export Section */}
          <div
            className="dropdown dropdown-end"
            style={isDownloading ? { pointerEvents: "none" } : {}}
          >
            <div
              tabIndex={isDownloading ? -1 : 0}
              role="button"
              className="btn btn-sm btn-primary"
              title="Export Document"
            >
              {isDownloading ? (
                <>
                  <span className="loading loading-spinner loading-xs"></span>
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <span>Export Document</span>
                  <LuChevronDown className="w-3 h-3 ml-1" />
                </>
              )}
            </div>
            <ul
              tabIndex={0}
              className="dropdown-content menu bg-base-100 rounded-box z-[200] w-44 p-2 shadow border border-base-200"
            >
              <li>
                <button
                  onClick={handleDocxDownload}
                  disabled={isDownloading}
                  className="flex items-center gap-2"
                >
                  {isDownloading ? (
                    <>
                      <span className="loading loading-spinner loading-xs"></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <LuDownload className="w-4 h-4" />
                      Export as Word
                    </>
                  )}
                </button>
              </li>
              <li>
                <button
                  onClick={handlePdfDownload}
                  disabled={isDownloading}
                  className="flex items-center gap-2"
                >
                  {isDownloading ? (
                    <>
                      <span className="loading loading-spinner loading-xs"></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <LuDownload className="w-4 h-4" />
                      Export as PDF
                    </>
                  )}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageUpload}
        onClose={() => setShowImageUpload(false)}
        onImageUploaded={handleImageUploaded}
        onImageUrl={handleImageUrl}
        sessionId={sessionId}
      />
      <AssetSelectorModal
        isOpen={showAssetSelector}
        onClose={() => setShowAssetSelector(false)}
        onAssetSelected={handleAssetSelected}
        sessionId={sessionId}
      />
    </>
  );
};

export default TiptapToolbar;
